import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TrackingScriptService } from '@nimbus-library/features/tracking/services/tracking-script.service';
import { ITrackingConfigurationService } from '@nimbus-library/features/tracking/services/i-tracking-configuration.service';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import {
  IPlanHatOptions,
  IPlanHatOptionsAccount,
  IPlanHatOptionsUser,
} from '@nimbus-library/features/tracking/models/i-planhat-options.model';
import { IPlanHatWindowService } from '@nimbus-library/features/tracking/services/i-planhat-window.service';
import { getClientContext } from '@nimbus-library/features/tracking/services/client-context.util';

export interface IPageUsageTrackingContext {
  tenantId?: string;
  userId?: string;
  userRoles?: Map<string, string>;
  crmId?: string;
  /** Embedding host the page runs in ("standalone", "teams", ...); supplied by the caller. */
  host?: string;
  /** Host-specific client type (e.g. Teams "web"/"desktop") when applicable. */
  hostClientType?: string;
  /**
   * Additional request headers to attach to the page usage POST (e.g. an auth header for apps
   * that authenticate API calls via a header rather than a same-origin cookie). When provided,
   * the request is always sent via fetch because navigator.sendBeacon cannot set custom headers.
   */
  requestHeaders?: Record<string, string>;
}

@Injectable()
export class TrackingIntegrationService {
  private readonly pageUsageEndpoint = '/api/user-settings/v1/usage/page';
  private readonly routeUrlGuidRegExp = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;
  private readonly windowPlanHatKey = 'plantrack';
  private readonly windowPlanHatOptionsKey = 'planhat_options';
  private readonly window: Window;
  private readonly scriptPlanHatSrc = '/scripts/planHat.js';
  private readonly scriptPlanHatKey = 'plantrack';
  private readonly isPlanHatLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private get planHat(): IPlanHatWindowService {
    return this.window[this.windowPlanHatKey];
  }

  private get planHatOptions(): IPlanHatOptions {
    return this.window[this.windowPlanHatOptionsKey];
  }

  private set planHatOptions(value: IPlanHatOptions) {
    this.window[this.windowPlanHatOptionsKey] = value;
  }

  private get isPlanHatLoaded(): boolean {
    return this.isPlanHatLoaded$.value;
  }

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly trackingConfigurationService: ITrackingConfigurationService,
    private readonly trackingScriptService: TrackingScriptService,
    private readonly logger: ILogger,
  ) {
    this.window = this.document.defaultView;
  }

  public start(account: IPlanHatOptionsAccount, user: IPlanHatOptionsUser, module?: string): Observable<boolean> {
    const configuration = this.trackingConfigurationService.configuration;

    if (!configuration.tenantUUID) {
      configuration.tenantUUID = '6f209b48-789b-41c0-80f6-21a299ac54bc'; // TODO: Remove default value when Atlas team add this id into environment configurations file
    }

    return this.trackingScriptService.load(this.scriptPlanHatSrc, this.scriptPlanHatKey).pipe(
      switchMap(
        () =>
          new Observable<boolean>((observer) => {
            this.planHat.load(configuration.tenantUUID, (error?: Error) => {
              if (error) {
                this.logger.error('PlanHat script loading failed:', error);
                this.isPlanHatLoaded$.next(false);
                observer.next(false);
                observer.complete();
              } else {
                this.planHatOptions = {
                  id: user.id,
                  email: user.id,
                  module: module || configuration.module,
                  name: user.name,
                  companyExternalId: account.id,
                  externalId: user.userId,
                  account,
                };

                this.planHat.identify(this.planHatOptions.id, this.planHatOptions);
                this.isPlanHatLoaded$.next(true);
                this.trackModule(this.planHatOptions.module);
                observer.next(true);
                observer.complete();
              }
            });
          }),
      ),
    );
  }

  public stop(): void {
    if (this.isPlanHatLoaded) {
      this.planHatOptions.id = null;
      this.planHatOptions.email = null;
      this.planHatOptions.externalId = null;
      this.planHat.identify(this.planHatOptions.id, this.planHatOptions);
      this.trackingScriptService.unload(this.scriptPlanHatKey);
    }
  }

  public updateUser(userRoles: Map<string, string>): void {
    if (this.isPlanHatLoaded) {
      userRoles.forEach((hasUserRole, userRole) => ((this.planHatOptions.userRoles || {})[userRole] = hasUserRole));
      this.planHat.identify(this.planHatOptions.id, this.planHatOptions);
      return;
    }
  }

  public trackEvent(eventName: string, properties?: { [key: string]: any }): void {
    if (!this.isPlanHatLoaded) {
      return;
    }
    this.planHat.track(eventName, properties);
  }

  public trackModule(module: string): void {
    const moduleNew = `${this.trackingConfigurationService.configuration.module}: ${module}`;

    if (this.isPlanHatLoaded && moduleNew !== this.planHatOptions.module) {
      this.planHatOptions.module = moduleNew;
      const data = {
        weight: 1,
        info: { module: this.planHatOptions.module },
      };
      this.planHat.track('Page_Visited', data);
      return;
    }
  }

  public trackPageUsage(page: string, routeUrl?: string, context: IPageUsageTrackingContext = {}): void {
    if (!routeUrl) {
      return;
    }

    try {
      const configuration = this.trackingConfigurationService.configuration;
      const clientContext = getClientContext(this.window);
      const payload = {
        app: configuration.module,
        page,
        routeUrl: this.sanitizeRouteUrl(routeUrl),
        module: `${configuration.module}: ${page}`,
        tenantId: context.tenantId,
        userId: context.userId,
        userRoles: this.toUserRolesObject(context.userRoles),
        crmId: context.crmId,
        operatingSystem: clientContext.operatingSystem,
        browser: clientContext.browser,
        locale: clientContext.locale,
        timezone: clientContext.timezone,
        host: context.host,
        hostClientType: context.hostClientType,
        ...(clientContext.pageLoadMs !== undefined ? { pageLoadMs: clientContext.pageLoadMs } : {}),
      };

      const requestHeaders = context.requestHeaders;

      // sendBeacon cannot attach custom headers, so it is only usable when the request authenticates
      // via a same-origin cookie. When an auth header is required, fall through to fetch instead.
      const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      if (!requestHeaders && this.window.navigator?.sendBeacon?.(this.pageUsageEndpoint, body)) {
        return;
      }

      this.window
        .fetch(this.pageUsageEndpoint, {
          body: JSON.stringify(payload),
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...requestHeaders,
          },
          keepalive: true,
          method: 'POST',
        })
        .catch((error) => this.logger.debug('Page usage tracking failed', error));
    } catch (error) {
      this.logger.debug('Page usage tracking failed', error);
    }
  }

  private sanitizeRouteUrl(routeUrl: string): string {
    return routeUrl.split(/[?#]/)[0].replace(this.routeUrlGuidRegExp, ':id');
  }

  private toUserRolesObject(userRoles?: Map<string, string>): { [key: string]: string } {
    const userRolesObject: { [key: string]: string } = {};
    userRoles?.forEach((hasUserRole, userRole) => (userRolesObject[userRole] = hasUserRole));
    return userRolesObject;
  }
}
